import AttributeValue from "@imminently/decisively-ui/AttributeValue";
import TemporalSliceProvider from "@imminently/decisively-ui/TemporalSliceProvider";
import TemporalValue from "@imminently/decisively-ui/TemporalValue";
import type * as React from "react";
import { ComponentPage, ComponentPreview, ComponentSection } from "../components/ComponentPage";

const temporalValue = {
  temporal: {
    ranges: [
      {
        r: {
          start: "-271821-04-20T00:00:00.000Z",
          end: "2025-02-17T00:00:00.000Z",
        },
        v: null,
      },
      {
        r: {
          start: "2025-02-17T00:00:00.000Z",
          end: "2025-02-22T00:00:00.000Z",
        },
        v: true,
      },
      {
        r: {
          start: "2025-02-22T00:00:00.000Z",
          end: "2025-02-24T00:00:00.000Z",
        },
        v: false,
      },
      {
        r: {
          start: "2025-02-24T00:00:00.000Z",
          end: "2025-03-01T00:00:00.000Z",
        },
        v: true,
      },
      {
        r: {
          start: "2025-03-01T00:00:00.000Z",
          end: "2025-03-03T00:00:00.000Z",
        },
        v: false,
      },
      {
        r: {
          start: "2025-03-03T00:00:00.000Z",
          end: "2025-03-04T00:00:00.000Z",
        },
        v: true,
      },
      {
        r: {
          start: "2025-03-04T00:00:00.000Z",
          end: "+275760-09-13T00:00:00.000Z",
        },
        v: null,
      },
    ],
  },
};

const mixedTemporalValue = {
  temporal: {
    ranges: [
      {
        r: {
          start: "-271821-04-20T00:00:00.000Z",
          end: "2025-01-01T00:00:00.000Z",
        },
        v: "pending",
      },
      {
        r: {
          start: "2025-01-01T00:00:00.000Z",
          end: "2025-02-01T00:00:00.000Z",
        },
        v: 1250.5,
      },
      {
        r: {
          start: "2025-02-01T00:00:00.000Z",
          end: "+275760-09-13T00:00:00.000Z",
        },
        v: "approved",
      },
    ],
  },
};

const largeTemporalValue = {
  temporal: {
    ranges: Array.from({ length: 60 }, (_, index) => {
      const start = new Date(Date.UTC(2025, 0, index + 1));
      const end = new Date(Date.UTC(2025, 0, index + 2));

      return {
        r: {
          start: index === 0 ? "-271821-04-20T00:00:00.000Z" : start.toISOString(),
          end: index === 59 ? "+275760-09-13T00:00:00.000Z" : end.toISOString(),
        },
        v: index % 4 === 0 ? null : index % 3 === 0 ? false : index % 5 === 0 ? `state ${index}` : true,
      };
    }),
  },
};

const examples = [
  { label: "String", node: { derived: "approved" } },
  { label: "Number", node: { derived: 1234.56789 } },
  { label: "Boolean true", node: { derived: true } },
  { label: "Boolean false", node: { derived: false } },
  { label: "Uncertain", node: { derived: null } },
  { label: "Date", node: { type: "date", derived: "2025-02-24T00:00:00.000Z" } },
  { label: "Set", node: { derived: { set: ["standard", true, 12] } } },
];

const ExampleGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
);

const ExampleTile = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex min-h-24 flex-col justify-between gap-4 rounded-md border border-border bg-card p-4">
    <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
    <div className="flex min-w-0 items-center">{children}</div>
  </div>
);

export function NodeValuePage() {
  return (
    <ComponentPage
      title="Node Value"
      description="Node value display for primitive, set, uncertain, date, and temporal values. Temporal examples follow the monitor SDK temporal range shape."
    >
      <ComponentSection
        title="AttributeValue"
        description="Renders node values as compact chips with type-specific styling and tooltips for precise values."
      >
        <ExampleGrid>
          {examples.map((example) => (
            <ExampleTile
              key={example.label}
              label={example.label}
            >
              <AttributeValue node={example.node} />
            </ExampleTile>
          ))}
        </ExampleGrid>
      </ComponentSection>

      <ComponentSection
        title="Temporal node value"
        description="A temporal node renders the switchable temporal display instead of a static chip."
      >
        <ComponentPreview className="justify-start">
          <div className="w-full max-w-3xl">
            <AttributeValue node={{ derived: temporalValue }} />
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Temporal display modes"
        description="The base temporal value can be forced into bar, chart, or text display for review."
      >
        <div className="space-y-4">
          <ExampleTile label="Bar">
            <div className="w-full max-w-3xl">
              <TemporalValue
                value={temporalValue}
                forceType="bar"
              />
            </div>
          </ExampleTile>
          <ExampleTile label="Chart">
            <div className="w-full max-w-3xl">
              <TemporalValue
                value={temporalValue}
                forceType="chart"
              />
            </div>
          </ExampleTile>
          <ExampleTile label="Tall chart">
            <div className="w-full max-w-3xl">
              <TemporalValue
                value={temporalValue}
                forceType="chart"
                temporalChartHeight={140}
              />
            </div>
          </ExampleTile>
          <ExampleTile label="Text">
            <div className="w-full max-w-3xl">
              <TemporalValue
                value={mixedTemporalValue}
                forceType="text"
                textProps={{ maxEntries: 2 }}
              />
            </div>
          </ExampleTile>
          <ExampleTile label="Large text">
            <div className="w-full max-w-3xl">
              <TemporalValue
                value={largeTemporalValue}
                forceType="text"
              />
            </div>
          </ExampleTile>
        </div>
      </ComponentSection>

      <ComponentSection
        title="AttributeValue temporal chart height"
        description="AttributeValue forwards temporal chart sizing to the temporal renderer."
      >
        <ComponentPreview className="justify-start">
          <div className="w-full max-w-3xl">
            <AttributeValue
              node={{ derived: temporalValue }}
              temporalChartHeight={140}
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Temporal slices"
        description="AttributeValue can render a temporal value as the value active at a specific date, either from a direct prop or TemporalSliceProvider."
      >
        <ExampleGrid>
          <ExampleTile label="2025-02-18">
            <AttributeValue
              node={{ derived: temporalValue }}
              temporalSlice="2025-02-18T00:00:00.000Z"
            />
          </ExampleTile>
          <ExampleTile label="2025-02-23">
            <AttributeValue
              node={{ derived: temporalValue }}
              temporalSlice="2025-02-23T00:00:00.000Z"
            />
          </ExampleTile>
          <ExampleTile label="2025-03-05">
            <AttributeValue
              node={{ derived: temporalValue }}
              temporalSlice="2025-03-05T00:00:00.000Z"
            />
          </ExampleTile>
          <ExampleTile label="Provider: 2025-02-23">
            <TemporalSliceProvider value="2025-02-23T00:00:00.000Z">
              <AttributeValue node={{ derived: temporalValue }} />
            </TemporalSliceProvider>
          </ExampleTile>
          <ExampleTile label="Prop overrides provider">
            <TemporalSliceProvider value="2025-02-18T00:00:00.000Z">
              <AttributeValue
                node={{ derived: temporalValue }}
                temporalSlice="2025-03-05T00:00:00.000Z"
              />
            </TemporalSliceProvider>
          </ExampleTile>
        </ExampleGrid>
      </ComponentSection>
    </ComponentPage>
  );
}
